import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
interface RulerVisualProps {
  length?: number;
  objectStart?: number;
  objectEnd?: number;
  unit?: 'cm' | 'mm';
  label?: string;
  mode?: 'stimulus' | 'support';
  hideValue?: boolean;
}
export function RulerVisual({
  length = 15,
  objectStart,
  objectEnd,
  unit = 'cm',
  label,
  mode = 'support',
  hideValue = false
}: RulerVisualProps) {
  const computed = useMemo(() => {
    const hasObject = objectStart !== undefined && objectEnd !== undefined;
    const objectLength = hasObject ? Math.abs(objectEnd - objectStart) : 0;
    const start = hasObject ? Math.min(objectStart, objectEnd) : 0;
    const end = hasObject ? Math.max(objectStart, objectEnd) : 0;
    // Use actual length instead of capping at 15
    const displayLength = length;
    return {
      hasObject,
      objectLength,
      start,
      end,
      displayLength
    };
  }, [length, objectStart, objectEnd]);
  const rulerViz = <div className="flex flex-col items-center gap-2 w-full" data-id="element-12490">
      {/* Object being measured (if provided) */}
      {computed.hasObject && <div className="relative w-full h-8" data-id="element-12491">
          <motion.div initial={{
        scaleX: 0,
        opacity: 0
      }} animate={{
        scaleX: 1,
        opacity: 1
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="absolute top-0 bg-blue-500 rounded-md shadow-sm" style={{
        left: `${computed.start / computed.displayLength * 100}%`,
        width: `${computed.objectLength / computed.displayLength * 100}%`,
        height: '24px',
        transformOrigin: 'left center'
      }} data-id="element-12492">
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-12493">
              <div className="bg-white/90 px-2 py-0.5 rounded text-[10px] font-bold text-blue-900" data-id="element-12494">
                {!hideValue || mode === 'support' ? `${computed.objectLength} ${unit}` : '?'}
              </div>
            </div>
          </motion.div>
        </div>}

      {/* Ruler */}
      <div className="w-full relative h-12 bg-slate-100 rounded-sm border border-slate-300 shadow-sm overflow-hidden" data-id="element-12495">
        {/* Centimeter markings */}
        {Array.from({
        length: computed.displayLength + 1
      }, (_, i) => {
        const leftPercent = i / computed.displayLength * 100;
        const isFive = i % 5 === 0;
        return <div key={`cm-${i}`} className="absolute top-0 flex flex-col items-center" style={{
          left: `${leftPercent}%`,
          transform: 'translateX(-50%)'
        }} data-id="element-12496">
                <div className={`bg-slate-600 ${isFive ? 'h-4 w-0.5' : 'h-3 w-px'}`} data-id="element-12497" />
                <span className="text-[10px] font-bold text-slate-700 mt-0.5" data-id="element-12498">
                  {i}
                </span>
              </div>;
      })}

        {/* Millimeter markings (between cm marks) */}
        {unit === 'mm' && Array.from({
        length: computed.displayLength * 10
      }, (_, i) => {
        if (i % 10 === 0) return null; // Skip cm marks
        const leftPercent = i / (computed.displayLength * 10) * 100;
        const isFive = i % 5 === 0;
        return <div key={`mm-${i}`} className="absolute top-0 bg-slate-400" style={{
          left: `${leftPercent}%`,
          height: isFive ? '8px' : '5px',
          width: '1px',
          transform: 'translateX(-50%)'
        }} data-id="element-12499" />;
      })}
      </div>
    </div>;
  if (mode === 'stimulus') {
    return <div className="bg-white rounded-xl p-4 border border-slate-200 w-full max-w-sm mx-auto" data-id="element-12500">
        {label && <p className="text-sm font-semibold text-slate-700 mb-3 text-center" data-id="element-12501">
            {label}
          </p>}
        {rulerViz}
      </div>;
  }
  return <div className="bg-white rounded-xl border border-slate-200 overflow-hidden w-full max-w-lg mx-auto shadow-sm" data-id="element-12502">
      <div className="bg-slate-50 px-4 py-3 border-b border-slate-200" data-id="element-12503">
        <h3 className="text-sm font-bold text-slate-800 text-center" data-id="element-12504">
          {label || 'Liniaal'}
        </h3>
      </div>

      <div className="p-6 bg-white" data-id="element-12505">{rulerViz}</div>

      <div className="bg-slate-50/50 p-4 border-t border-slate-100" data-id="element-12506">
        <p className="text-sm text-slate-700 text-center leading-relaxed" data-id="element-12507">
          {computed.hasObject ? <>
              Het blok begint bij{' '}
              <strong data-id="element-12508">
                {computed.start} {unit}
              </strong>{' '}
              en eindigt bij{' '}
              <strong data-id="element-12509">
                {computed.end} {unit}
              </strong>
              . Lengte = {computed.end} − {computed.start} ={' '}
              <strong data-id="element-12510">
                {computed.objectLength} {unit}
              </strong>
              .
            </> : <>
              Dit is een liniaal om lengtes te meten. Elk groot streepje is 1
              cm.
            </>}
        </p>
      </div>
    </div>;
}